/**
 * PlayCanvas Configurator — types and route mapping for boat configurator.
 *
 * The PlayCanvas engine exposes `window.configurator` with these methods:
 *   - setConfig(partialConfig)       — change materials / boat type
 *   - getConfig()                    — current state
 *   - getAvailableOptions()          — all available options per boat
 *   - subscribe(cb)                  — listen for state changes
 *   - sceneAdapter.getOptionsForBoat(boatType) — options for a specific boat
 */

// ─── Configurator API types ──────────────────────────────────────

export interface MaterialOption {
  value: string;
  label: string;
}

export interface PropertyOptions {
  label: string;
  options: MaterialOption[];
}

export interface AvailableOptionsEntry {
  type: string;
  options?: MaterialOption[];
  perBoat?: Record<string, MaterialOption[]>;
}

export interface PlayCanvasConfig {
  boatType: string;
  railing: string | null;
  biminiFrame: string | null;
  upperPanelColor: string | null;
  lowerPanelColor: string | null;
  furnitureBaseColor: string | null;
  furnitureAccentColor: string | null;
  fiberglass: string | null;
  canvas: string | null;
  flooring: string | null;
  accentColor?: string | null;
  [key: string]: string | null | undefined;
}

export interface PlayCanvasConfigurator {
  setConfig: (partialConfig: Partial<PlayCanvasConfig>) => Promise<void>;
  getConfig: () => PlayCanvasConfig;
  getState?: () => PlayCanvasConfig;
  getAvailableOptions: () => Record<string, AvailableOptionsEntry>;
  subscribe: (
    callback: (newState: PlayCanvasConfig, oldState: PlayCanvasConfig) => void
  ) => () => void;
  sceneAdapter?: {
    getOptionsForBoat: (boatType: string) => Record<string, PropertyOptions>;
  };
}

// ─── Route → boatType mapping ────────────────────────────────────
// Maps route param `:id` (from boatList / assetsMap keys) to PlayCanvas boatType.

export const ROUTE_TO_BOAT_TYPE: Record<string, string> = {
  // Sunsation variants
  Sunsation_RL_230_CL: 'sunsation',
  Sunsation_RC_230_DL: 'sunsation',
  Sunspree_RF_230_CL: 'sunsation',

  // Solaris
  Solaris_REV_250_CL: 'solaris',

  // Intrigue variants
  'intrigue-rl-230-cl': 'intrigue',
  'intrigue-rf-250-cl': 'intrigue',
  Intrigue_Rev_270_CL: 'intrigue',

  // Escalante
  Escalante_310_CL: 'escalante',
};

/**
 * All PlayCanvas material property keys (excluding boatType).
 * Used to iterate over configurable attributes.
 */
export const MATERIAL_PROPERTY_KEYS = [
  'railing',
  'biminiFrame',
  'upperPanelColor',
  'lowerPanelColor',
  'furnitureBaseColor',
  'furnitureAccentColor',
  'fiberglass',
  'canvas',
  'flooring',
  'accentColor',
] as const;

/**
 * Human-readable labels for PlayCanvas property keys.
 */
export const PROPERTY_LABELS: Record<string, string> = {
  railing: 'Rail Color',
  biminiFrame: 'Bimini Frame',
  upperPanelColor: 'Upper Panel Color',
  lowerPanelColor: 'Lower Panel Color',
  furnitureBaseColor: 'Base Color',
  furnitureAccentColor: 'Accent Color',
  fiberglass: 'Fiberglass',
  canvas: 'Canvas Color',
  flooring: 'Flooring Color',
  accentColor: 'Accent Color',
};

/**
 * Maps PlayCanvas property keys → paramsConfig section id.
 * Used in the reducer to look up swatch images from paramsConfig (legacy fallback).
 */
export const PLAYCANVAS_PROP_TO_SECTION_ID: Record<string, string> = {
  railing: 'rail_color__section',
  upperPanelColor: 'top_panel_color__sections',
  lowerPanelColor: 'bottom_panel_color_section',
  furnitureBaseColor: 'base_color_selection',
  furnitureAccentColor: 'accent_color_selection',
  fiberglass: 'fiberglass_color__section',
  canvas: 'cover_color__section',
  flooring: 'flooring_color__sections',
  // biminiFrame — немає секції з картинками в paramsConfig
};

/**
 * Direct image map: PlayCanvas property key → option value → imgUrl.
 * All images from /img/Icon/ folder (%20 for spaces in folder names).
 */
export const PLAYCANVAS_IMAGE_MAP: Record<string, Record<string, string>> = {
  railing: {
    'Midnight': '/img/Icon/Railing/Midnight.jpg',
    'Moonstone': '/img/Icon/Railing/Moonstone.jpg',
    'Granite': '/img/Icon/Railing/Granite.jpg',
    'Black_Hammered': '/img/Icon/Railing/Black-Hammered.jpg',
    'Sapphire_Blue': '/img/Icon/Railing/Sapphire-Blue.jpg',
    'Red': '/img/Icon/Railing/Red.jpg',
    'White': '/img/Icon/Railing/White.jpg',
    'Gunmetal Gray': '/img/Icon/Railing/Gunmetal-Gray.jpg',
  },
  biminiFrame: {
    'Midnight': '/img/Icon/Bimini%20Frame/Midnight.jpg',
    'Satine Silver': '/img/Icon/Bimini%20Frame/Satin-Silver.jpg',
  },
  accentColor: {
    'Pearl Loop': '/img/Icon/ACCENT%20COLORS/Pearl-Loop.jpg',
    'Twilight_Twill': '/img/Icon/ACCENT%20COLORS/Twilight-Twill.jpg',
    'White Cap': '/img/Icon/ACCENT%20COLORS/White-Cap.jpg',
    'Dark Mist': '/img/Icon/ACCENT%20COLORS/Dark-Mist.jpg',
    'Light Mist': '/img/Icon/ACCENT%20COLORS/Light-Mist.jpg',
    'Midnight': '/img/Icon/ACCENT%20COLORS/Midnight.jpg',
    'Terra Clay': '/img/Icon/ACCENT%20COLORS/Terra-Clay.jpg',
  },
  upperPanelColor: {
    'Black': '/img/Icon/Panel%20Color/Black.jpg',
    'White Pearl': '/img/Icon/Panel%20Color/White-Pearl.jpg',
    'Champagne': '/img/Icon/Panel%20Color/Champagne.jpg',
    'Graphite': '/img/Icon/Panel%20Color/Graphite.jpg',
    'Nautical Blue': '/img/Icon/Panel%20Color/Nautical-Blue.jpg',
    'Crimson Red': '/img/Icon/Panel%20Color/Crimson-Red.jpg',
  },
  lowerPanelColor: {
    'Black': '/img/Icon/Panel%20Color/Black.jpg',
    'White Pearl': '/img/Icon/Panel%20Color/White-Pearl.jpg',
    'Champagne': '/img/Icon/Panel%20Color/Champagne.jpg',
    'Graphite': '/img/Icon/Panel%20Color/Graphite.jpg',
    'Nautical Blue': '/img/Icon/Panel%20Color/Nautical-Blue.jpg',
    'Crimson Red': '/img/Icon/Panel%20Color/Crimson-Red.jpg',
  },
  furnitureBaseColor: {
    // sunsation
    'Dessert_Sand_main': '/img/Icon/Furniture%20Base%20Color/Desert-Sand-Main.jpg',
    'Nightfall_Cool_Touch_main': '/img/Icon/Furniture%20Base%20Color/Nightfall-Cool-Touch-Main.jpg',
    'Firewood_Cool_Touch_main': '/img/Icon/Furniture%20Base%20Color/Firewood-Cool-Touch-Main.jpg',
    'Mountain_Grey_main': '/img/Icon/Furniture%20Base%20Color/Mountain-Gray-Main.jpg',
    // solaris
    'Sandbar_main': '/img/Icon/Furniture%20Base%20Color/Sandbar-Main.jpg',
    'Firewood_main': '/img/Icon/Furniture%20Base%20Color/Firewood-Main.jpg',
    'Overcast_main': '/img/Icon/Furniture%20Base%20Color/Overcast-Main.jpg',
    'Nightfall_main': '/img/Icon/Furniture%20Base%20Color/Nightfall-Main.jpg',
    // intrigue
    'White Cap': '/img/Icon/Furniture%20Base%20Color/White-Cap.jpg',
    'Dark Mist': '/img/Icon/ACCENT%20COLORS/Dark-Mist.jpg',
    'Light Mist': '/img/Icon/ACCENT%20COLORS/Light-Mist.jpg',
    'Midnight': '/img/Icon/Furniture%20Base%20Color/Midnight.jpg',
    'Terra Clay': '/img/Icon/Furniture%20Base%20Color/Terra-Clay.jpg',
    // escalante
    'Stone': '/img/Icon/Furniture%20Base%20Color/Stone.jpg',
    'Cream': '/img/Icon/Furniture%20Base%20Color/Cream.jpg',
    'Slate': '/img/Icon/Furniture%20Base%20Color/Slate.jpg',
    'Toffee': '/img/Icon/Furniture%20Base%20Color/Toffee.jpg',
    'Cognac': '/img/Icon/Furniture%20Base%20Color/Cognac.jpg',
    'Blonde': '/img/Icon/Furniture%20Base%20Color/Blonde.jpg',
  },
  furnitureAccentColor: {
    // sunsation
    'Hazel Tweed': '/img/Icon/ACCENT%20COLORS/Hazel-Tweed.jpg',
    'Bronze_Weave': '/img/Icon/ACCENT%20COLORS/Bronze-Weave.jpg',
    'Arctic': '/img/Icon/ACCENT%20COLORS/Arctic.jpg',
    'Ash Tweed': '/img/Icon/ACCENT%20COLORS/Ash-Tweed.jpg',
    'Carbon_Weave': '/img/Icon/ACCENT%20COLORS/Carbon-Weave.jpg',
    'Nautical_Blue_Diamond': '/img/Icon/ACCENT%20COLORS/Nautical-Blue-Diamond.jpg',
    'Red_Diamond': '/img/Icon/ACCENT%20COLORS/Crimson-Red-Diamond.jpg',
    // solaris
    'Arctic_Diamond': '/img/Icon/ACCENT%20COLORS/Arctic-Diamond.jpg',
    'Arctic Diamond': '/img/Icon/ACCENT%20COLORS/Arctic-Diamond.jpg',
    'Twilight_Twill': '/img/Icon/ACCENT%20COLORS/Twilight-Twill.jpg',
    'Twilight Twill': '/img/Icon/ACCENT%20COLORS/Twilight-Twill.jpg',
    'Bronze Weave': '/img/Icon/ACCENT%20COLORS/Bronze-Weave.jpg',
    'Quilted': '/img/Icon/ACCENT%20COLORS/Quilted.jpg',
    'Carbon Weave': '/img/Icon/ACCENT%20COLORS/Carbon-Weave.jpg',
    // escalante
    'Stone': '/img/Icon/ACCENT%20COLORS/Stone.jpg',
    'Cream': '/img/Icon/ACCENT%20COLORS/Cream.jpg',
    'Slate': '/img/Icon/ACCENT%20COLORS/Slate.jpg',
    'Toffee': '/img/Icon/ACCENT%20COLORS/Toffee.jpg',
    'Cognac': '/img/Icon/ACCENT%20COLORS/Cognac.jpg',
    'Blonde': '/img/Icon/ACCENT%20COLORS/Blonde.jpg',
  },
  fiberglass: {
    'Desert_Sand': '/img/Icon/FIBERGLASS/Desert-Sand.jpg',
    'Mountain_Grey': '/img/Icon/FIBERGLASS/Mountain-Gray.jpg',
    'Black': '/img/Icon/FIBERGLASS/Black.jpg',
    'Gunmetal Gray': '/img/Icon/FIBERGLASS/Gunmetal-Gray.jpg',
    'White': '/img/Icon/FIBERGLASS/White.jpg',
  },
  canvas: {
    'Harbor_Gray': '/img/Icon/CANVAS/Harbor-Gray.jpg',
    'Nautical_Blue': '/img/Icon/CANVAS/Nautical-Blue.jpg',
    'Onyx': '/img/Icon/CANVAS/Onyx.jpg',
  },
  flooring: {
    'Sterling_Shores': '/img/Icon/FLOORING/Sterling-Shores.jpg',
    'Slate_Teak': '/img/Icon/FLOORING/Slate-Teak.jpg',
    'Mahogany_Teak': '/img/Icon/FLOORING/Mahogany-Teak.jpg',
    'Driftwood': '/img/Icon/FLOORING/Driftwood.jpg',
    'Double_Desert_Teak': '/img/Icon/FLOORING/Double-Desert-Teak.jpg',
    'Birchwood': '/img/Icon/FLOORING/Birchwood.jpg',
  },
};

// ─── Sync helper ─────────────────────────────────────────────────
/**
 * Read the current PlayCanvas config and emit each material property
 * to a callback so the caller can dispatch lightweight Redux updates.
 *
 * Usage inside a React component:
 * ```
 * syncStoreFromPlayCanvasConfig((attrName, value) =>
 *   dispatch(setActiveItem({ name: attrName, data: value }))
 * );
 * ```
 */
export function syncStoreFromPlayCanvasConfig(
  cb: (propertyKey: string, value: string) => void
): void {
  const config = window.configurator?.getConfig?.();
  if (!config) return;

  for (const key of MATERIAL_PROPERTY_KEYS) {
    const val = config[key];
    if (val != null) {
      cb(key, val);
    }
  }
}

// ─── Global window type augmentation ─────────────────────────────
declare global {
  interface Window {
    configurator?: PlayCanvasConfigurator;
    player?: any;
  }
}
